/* eslint-disable */
// HifzWifaqAnalytics — new sub-tab for HifzManagement.
// Works for EVERY hifz student:
//   • in-progress  → projects hafiz-completion date from real sabaq pace,
//                    then judges Wifaq-readiness = (finish hifz + girdan buffer) <= exam date
//   • already hafiz → full girdan/manzil readiness score
// Drop in:  <HifzWifaqAnalytics students={students} targetYears={targetYears} />
import React from "react";
import { supabase } from "./supabaseClient";

const PAGES_PER_QURAN = 604;
const PAGES_PER_JUZ   = PAGES_PER_QURAN / 30; // ~20.1
const HIFZ_SUBS = ["Tajweed","Makharij","Memorization","Manzil","Hifz","Revision","Nazra"];
const SUB_WEIGHT = { Tajweed: 1.5, Makharij: 1.5 };
const GIRDAN_TARGET = 3;     // full revisions wanted before sitting (hafiz path)
const WINDOW_DAYS   = 28;    // pace measurement window
const DEFAULT_BUFFER_WEEKS = 16; // time after completing hifz to do girdan before Wifaq

function examStrengthOf(results) {
  const latest = {};
  for (const r of results || []) {
    if (!r.total_marks) continue;
    if (!HIFZ_SUBS.some(h => r.subject?.toLowerCase().includes(h.toLowerCase()))) continue;
    const t = new Date(r.exam_date || r.created_at || 0).getTime();
    if (!latest[r.subject] || t >= latest[r.subject]._t) latest[r.subject] = { ...r, _t: t };
  }
  let wSum = 0, wTot = 0;
  for (const r of Object.values(latest)) {
    const pct = (r.marks_obtained / r.total_marks) * 100, w = SUB_WEIGHT[r.subject] || 1;
    wSum += pct * w; wTot += w;
  }
  return wTot ? wSum / wTot : 0;
}

function analyse({ journey, logs, results, examDate, bufferWeeks }) {
  const j = journey || {};
  const juzDone = Number(j.juz_completed) || 0;
  const isHafiz = j.is_hafiz === true || juzDone >= 30;
  const pct = Math.min(juzDone / 30, 1) * 100;
  const now = Date.now();

  const recent = (logs || []).filter(l => new Date(l.date).getTime() >= now - WINDOW_DAYS * 864e5);
  const sumP = (arr, k) => arr.reduce((s, l) => s + (Number(l[k]) || 0), 0);

  // memorization pace (juz/day): prefer recent sabaq, else lifetime from start date
  const recentSabaqJuzDay = sumP(recent, "sabaq_pages") / WINDOW_DAYS / PAGES_PER_JUZ;
  let lifetimeJuzDay = 0;
  if (j.hifz_start_date) {
    const days = Math.max(1, (now - new Date(j.hifz_start_date)) / 864e5);
    lifetimeJuzDay = juzDone / days;
  }
  const paceJuzDay = recentSabaqJuzDay > 0 ? recentSabaqJuzDay : lifetimeJuzDay;
  const memoPaceMonth = paceJuzDay * 30; // juz per month, for display

  // projected hafiz completion date
  const juzRemaining = Math.max(30 - juzDone, 0);
  const daysToHafiz = paceJuzDay > 0 ? juzRemaining / paceJuzDay : null;
  const hafizDate = isHafiz ? "done" : (daysToHafiz != null ? new Date(now + daysToHafiz * 864e5) : null);

  // girdan / revision (hafiz path)
  const totalManzil = sumP(logs || [], "manzil_pages");
  const girdansDone = Number.isFinite(j.girdans_completed) ? j.girdans_completed : totalManzil / PAGES_PER_QURAN;
  const manzilPaceWeek = sumP(recent, "manzil_pages") / (WINDOW_DAYS / 7);

  const examTime = examDate ? new Date(examDate).getTime() : null;
  const bufferDays = (bufferWeeks ?? DEFAULT_BUFFER_WEEKS) * 7;

  let status, score = null, sub = "";

  if (isHafiz) {
    // full readiness score
    const completion = 100;
    const strength = examStrengthOf(results);
    const girdanDepth = Math.min(girdansDone / GIRDAN_TARGET, 1) * 100;
    const weeksToExam = examTime ? Math.max((examTime - now) / 864e5, 0) / 7 : 0;
    const pagesRemaining = PAGES_PER_QURAN * Math.max(GIRDAN_TARGET - girdansDone, 0);
    const required = weeksToExam > 0 ? pagesRemaining / weeksToExam : (pagesRemaining > 0 ? Infinity : 0);
    const paceAdq = required <= 0 ? 100 : Math.min(manzilPaceWeek / required, 1) * 100;
    score = Math.round(0.30 * completion + 0.25 * strength + 0.20 * girdanDepth + 0.25 * paceAdq);
    score = Math.max(0, Math.min(100, score));
    if (manzilPaceWeek >= required && score >= 70) status = { label: "Hafiz · on track", tone: "#1a6b3c", urgency: 4 };
    else if (required - manzilPaceWeek <= 1.2 * PAGES_PER_JUZ) status = { label: "Hafiz · at risk", tone: "#d97706", urgency: 1 };
    else status = { label: "Hafiz · behind", tone: "#dc2626", urgency: 0.5 };
    sub = `${girdansDone.toFixed(1)} girdans · exam avg ${Math.round(strength)}%`;
  } else if (paceJuzDay <= 0 || !hafizDate) {
    status = { label: "No pace data", tone: "#9ca3af", urgency: 3 };
    sub = "needs sabaq logs or start date";
  } else if (!examTime) {
    status = { label: "On pace", tone: "#2563eb", urgency: 2 };
    sub = "set an exam date to judge readiness";
  } else {
    const readyTime = hafizDate.getTime() + bufferDays * 864e5;
    if (readyTime <= examTime)      status = { label: "Projected ready", tone: "#1a6b3c", urgency: 2 };
    else if (hafizDate.getTime() <= examTime) status = { label: "Hifz in time · girdan tight", tone: "#d97706", urgency: 1 };
    else                            status = { label: "Won't finish in time", tone: "#dc2626", urgency: 0 };
    sub = `${juzRemaining} juz left at ${memoPaceMonth.toFixed(1)} juz/mo`;
  }

  return { juzDone, pct, isHafiz, memoPaceMonth, hafizDate, score, status, sub,
    logsInWindow: recent.length, stage: j.stage || (isHafiz ? "hafiz" : "hifz") };
}

const fmtDate = d => d instanceof Date ? d.toLocaleDateString("en-GB", { month: "short", year: "numeric" }) : (d === "done" ? "✓ Hafiz" : "—");

export default function HifzWifaqAnalytics({ students = [], targetYears = 3.5, compact = false }) {
  const [raw, setRaw]   = React.useState(null);
  const [examDate, setExamDate] = React.useState("");
  const [buffer, setBuffer]     = React.useState(DEFAULT_BUFFER_WEEKS);
  const [loading, setLoad]      = React.useState(true);
  const [error, setError]       = React.useState(null);

  React.useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoad(true); setError(null);
        const today = new Date().toISOString().slice(0, 10);
        const [jr, lr, rr, er] = await Promise.all([
          supabase.from("hifz_journey").select("*"),
          supabase.from("hifz_daily_log").select("student_id,date,sabaq_pages,manzil_pages"),
          supabase.from("results").select("student_id,subject,marks_obtained,total_marks,created_at"),
          supabase.from("exams").select("*").gte("exam_date", today).order("exam_date", { ascending: true }),
        ]);
        for (const r of [jr, lr, rr, er]) if (r.error) throw r.error;
        if (!alive) return;
        const exams = er.data || [];
        const target = exams.find(e => e.name?.toLowerCase().includes("wifaq")) || exams[0] || null;
        setExamDate(target?.exam_date || "");
        setRaw({ journeys: jr.data || [], logs: lr.data || [], results: rr.data || [] });
      } catch (e) {
        if (alive) setError(e.message || String(e));
      } finally {
        if (alive) setLoad(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const rows = React.useMemo(() => {
    if (!raw) return [];
    const jMap = {}; for (const j of raw.journeys) jMap[String(j.student_id)] = j;
    const group = (arr) => { const m = {}; for (const x of arr) (m[String(x.student_id)] ||= []).push(x); return m; };
    const lMap = group(raw.logs), rMap = group(raw.results);
    const hifz = students.filter(s => {
      const st = (s.stream || "").toLowerCase();
      return jMap[String(s.id)] || st.includes("hifz") || st.includes("both");
    });
    const out = hifz.map(s => ({
      id: s.id,
      name: (s.name || s.full_name || "Unknown").split(" ").slice(0, 3).join(" "),
      cls: s.class || s["class"] || "—",
      ...analyse({ journey: jMap[String(s.id)], logs: lMap[String(s.id)] || [], results: rMap[String(s.id)] || [], examDate, bufferWeeks: buffer }),
    }));
    out.sort((a, b) => a.status.urgency - b.status.urgency || a.juzDone - b.juzDone);
    return out;
  }, [raw, students, examDate, buffer]);

  const count = (label) => rows.filter(r => r.status.label === label).length;
  const summary = [
    ["Projected ready", count("Projected ready") + count("Hafiz · on track"), "#1a6b3c", "#f0fdf4"],
    ["Tight / at risk", count("Hifz in time · girdan tight") + count("Hafiz · at risk"), "#d97706", "#fffbeb"],
    ["Won't make it", count("Won't finish in time") + count("Hafiz · behind"), "#dc2626", "#fef2f2"],
    ["In hifz total", rows.length, "#7c3aed", "#f5f3ff"],
  ];

  if (compact) {
    return (
      <div style={{ background: "#f5f3ff", borderRadius: 14, padding: "14px 16px", border: "1px solid #c4b5fd" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <div style={{ fontWeight: 800, fontSize: 14, color: "#7c3aed" }}>🕌 Wifaq Readiness</div>
          <div style={{ fontSize: 11, color: "#9ca3af" }}>full view in Hifz tab</div>
        </div>
        {loading && <div style={{ color: "#9ca3af", fontSize: 13 }}>Loading…</div>}
        {error && <div style={{ color: "#b91c1c", fontSize: 12 }}>⚠ {error}</div>}
        {!loading && !error && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(110px,1fr))", gap: 10 }}>
            {summary.map(([l, v, col, bg]) => (
              <div key={l} style={{ background: bg, borderRadius: 12, padding: "10px 12px", border: "1px solid #ede9fe" }}>
                <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 3 }}>{l}</div>
                <div style={{ fontSize: 22, fontWeight: 900, color: col }}>{v}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  const labelCss = { background: "#fff", borderRadius: 10, padding: "9px 10px", border: "1px solid #c4b5fd", fontSize: 13 };

  return (
    <div className="fade-up" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ background: "#f5f3ff", borderRadius: 14, padding: "16px 18px", border: "1px solid #c4b5fd" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
          <div>
            <div style={{ fontWeight: 900, fontSize: 17, color: "#7c3aed" }}>🕌 Wifaq Readiness & Hifz Projection</div>
            <div style={{ fontSize: 12, color: "#6b21a8", marginTop: 2 }}>Projects each student's hafiz date from pace, then readiness for the exam</div>
          </div>
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <label style={{ fontSize: 12, fontWeight: 700, color: "#6b21a8" }}>Exam date
              <input type="date" value={examDate} onChange={e => setExamDate(e.target.value)} style={{ marginLeft: 6, padding: "5px 8px", borderRadius: 8, border: "1px solid #c4b5fd", fontSize: 13 }} />
            </label>
            <label style={{ fontSize: 12, fontWeight: 700, color: "#6b21a8" }}>Girdan buffer
              <input type="number" min={0} max={52} value={buffer} onChange={e => setBuffer(Number(e.target.value) || 0)} style={{ marginLeft: 6, width: 56, padding: "5px 8px", borderRadius: 8, border: "1px solid #c4b5fd", fontSize: 13 }} /> wks
            </label>
          </div>
        </div>
      </div>

      {loading && <div style={{ padding: 20, color: "#9ca3af", fontSize: 13 }}>Loading analytics…</div>}
      {error && (
        <div style={{ padding: "12px 14px", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 10, color: "#b91c1c", fontSize: 13 }}>
          ⚠ Couldn't load: {error}<br />
          <span style={{ fontSize: 12, color: "#7f1d1d" }}>Usually an RLS policy or a missing table/column — check the table named above.</span>
        </div>
      )}
      {!loading && !error && rows.length === 0 && (
        <div style={{ padding: 20, color: "#6b7280", fontSize: 13 }}>No hifz students found (need stream “Hifz”/“Both” or a hifz_journey row).</div>
      )}

      {!loading && !error && rows.length > 0 && (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(120px,1fr))", gap: 10 }}>
            {summary.map(([l, v, col, bg]) => (
              <div key={l} style={{ background: bg, borderRadius: 12, padding: "12px 14px", border: "1px solid #ede9fe" }}>
                <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 3 }}>{l}</div>
                <div style={{ fontSize: 24, fontWeight: 900, color: col }}>{v}</div>
              </div>
            ))}
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {rows.map(r => (
              <div key={r.id} style={{ background: "#fff", borderRadius: 12, padding: "12px 14px", border: "1px solid #ede9fe", display: "grid", gridTemplateColumns: "1.4fr 1fr 1fr 1.1fr 1.3fr", gap: 12, alignItems: "center" }}>
                <div>
                  <div style={{ fontWeight: 800, fontSize: 14, color: "#1f2937" }}>{r.name}</div>
                  <div style={{ fontSize: 11, color: "#9ca3af" }}>{r.cls} · {r.stage}</div>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 3 }}>{r.juzDone}/30 juz</div>
                  <div style={{ height: 6, background: "#ede9fe", borderRadius: 3, overflow: "hidden" }}>
                    <div style={{ height: "100%", width: `${r.pct}%`, background: "#7c3aed", borderRadius: 3 }} />
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: "#9ca3af" }}>Memo pace</div>
                  <div style={{ fontSize: 14, fontWeight: 800, color: "#1f2937" }}>{r.memoPaceMonth.toFixed(1)}<span style={{ fontSize: 10, color: "#9ca3af" }}> juz/mo</span></div>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: "#9ca3af" }}>{r.isHafiz ? "Readiness" : "Proj. hafiz"}</div>
                  <div style={{ fontSize: 14, fontWeight: 800, color: r.status.tone }}>{r.isHafiz ? `${r.score}%` : fmtDate(r.hafizDate)}</div>
                </div>
                <div>
                  <div style={{ display: "inline-block", background: r.status.tone, color: "#fff", borderRadius: 8, padding: "3px 9px", fontSize: 12, fontWeight: 700 }}>{r.status.label}</div>
                  <div style={{ fontSize: 10, color: "#9ca3af", marginTop: 3 }}>{r.sub}{r.logsInWindow < 5 ? " ⚠ sparse logs" : ""}</div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
